import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { get, sumBy, isEmpty } from 'lodash'

import {
  startOnlineCardOrder,
  startTabOrderAdjustment,
  startTabOrderAdjustmentAndClose,
  startTabWithRemotePayment,
  startCloseTabOrder,
  applyOrderExemptions,
  startCashOrder
} from '../../actions/order'
import { clearRemoteOrderTotal, getRemoteOrderTotal } from '../../actions/orderTotalRemote'
import { printOrder, fetchPrinters } from '../../actions/printer'
import { clearCart } from '../../actions/cart'

import { getSubtotal, getTotal, getCartQuantity2, makeGetCartItems } from '../../selectors/cart'
import { makeGetTabOrder, getFinalOrderState, makeGetUnsyncedLocalIds, } from '../../selectors/order'
import { makeGetRemoteOrderTotal } from '../../selectors/orderTotalRemote'
import { makeGetCurrentMenu } from '../../selectors/menus'
import { makeGetActiveRevenueCenter } from '../../selectors/revenueCenter'
import { getActiveCustomer } from '../../selectors/customer'
import { makeGetOrderPreferencesByOrder, makeGetActiveCustomerPreferences, suiteCustomerIsOrdering, getPaymentByOrder } from '../../selectors/suites'
import { getPrinters } from '../../selectors/printer'
import { getIsSuitesMode, getReceiptPrinterUuid } from '../../selectors/config'
import { getPeripheralStatus } from '../../selectors/peripheral'
import { getDiscountsAvailable } from '../../selectors/promotion'

import { makeExemptionOverridesFromOrder } from '../../utils/exemptionUtils'
import { formatCartItemModifiers, displayCartItemPrice } from '../../utils/formatters'
import OrderStates, { ORDER_STATES } from '../../utils/orderStates'

import CartDetailsViewModel from '../../viewmodels/CartDetailsViewModel'
import { TabOrderDetailsViewModel } from '../../viewmodels/TabOrderViewModel'
import CartItemsViewModel from '../../viewmodels/CartItemsViewModel'

import TabOrderPayNowModal from '../../components/tabs/TabOrderPayNowModal'

const mappedCartItems = cartItems => {
  const result = cartItems.map(item => {
    const modifiers = get(item, 'modifiers', [])
    const notes = get(item, 'notes', null)
    return {
      ...item,
      quantity: get(item, "quantity", 1),
      priceInCents: get(item, "defaultPriceInCents", 0),
      price: displayCartItemPrice(item, modifiers),
      modifiersString: formatCartItemModifiers(modifiers, notes)
    }
  })
  return result
}

const newOrder = (state, ownProps) => {
  const getCartItems = makeGetCartItems()
  const cartItems = getCartItems(state)
  const items = CartItemsViewModel(cartItems)
  const subtotal = getSubtotal(state)
  const total = getTotal(state)
  const itemCount = getCartQuantity2(state)
  const unsyncedLocalIds = makeGetUnsyncedLocalIds()(state)

  return {
    ...CartDetailsViewModel({ subtotal, total }),
    itemCount,
    items,
    unsyncedLocalIds,
  }
}

const joinedOrders = (state, ownProps) => {
  // Cart selectors
  const getCartItems = makeGetCartItems()
  const cartItems = getCartItems(state)
  const items = mappedCartItems(cartItems)

  // Order Selectors
  const orderId = get(ownProps, 'match.params.orderId', '')
  const getTabOrder = makeGetTabOrder()
  const order = getTabOrder(state, orderId)
  const newItems = [...order.itemModels, ...items]
  const newItemCount = newItems.length
  const newSubtotal = sumBy(newItems, (newItem) => {
    return get(newItem, 'quantity', 0) * get(newItem, 'priceInCents', 0)
  })

  const newOrder = {
    ...order,
    amountInCents: newSubtotal,
    itemModels: newItems,
    itemCount: newItemCount,
  }

  return TabOrderDetailsViewModel(newOrder)
}

const existingOrder = (state, ownProps) => {
  const orderId = get(ownProps, 'match.params.orderId', '')
  const getTabOrder = makeGetTabOrder()
  const order = TabOrderDetailsViewModel(getTabOrder(state, orderId))

  return { ...order }
}

const mapStateToProps = (state, ownProps) => {
  const orderId = get(ownProps, 'match.params.orderId', undefined)
  const isNewOrder = orderId === undefined || orderId === 'pay-now'
  const getTabOrder = makeGetTabOrder()
  const order = getTabOrder(state, orderId)

  const { isRemotePayment } = order
  const selectRemoteOrderTotal = makeGetRemoteOrderTotal()
  const remoteOrderTotal = selectRemoteOrderTotal(state)
  const revenueCenter = makeGetActiveRevenueCenter()(state)
  const customer = getActiveCustomer(state)
  const getActiveCustomerPreferences = makeGetActiveCustomerPreferences()
  const getOrderPreferencesByOrder = makeGetOrderPreferencesByOrder()
  const isSuiteCustomer = suiteCustomerIsOrdering(state)
  const isSuitesMode = getIsSuitesMode(state)
  const displayName = `${revenueCenter.name || 'No Suite'} ${customer.name || 'Anonymous'}`
  const finalOrderState = getFinalOrderState(state, order)
  const printers = getPrinters(state)
  const deviceReceiptPrinterUuid = getReceiptPrinterUuid(state)
  const peripheralStatus = getPeripheralStatus(state)
  const discountsAvailable = getDiscountsAvailable(state)
  const cashEnabled =  revenueCenter?.allowCashPaymentsOnPos ?? true
  const noOpPayments = revenueCenter?.customPaymentTypes ?? []
  const menu = makeGetCurrentMenu()(state)
  const isNoTipMenu = isEmpty(menu?.tipSuggestions)

  let orderingPreference = {}
  let paymentOptions = []
  let paymentOnOrder = undefined
  let exemptionOverrides = undefined

  if (isSuiteCustomer) {
    let menuSubtype = null

    if (isNewOrder) {
      orderingPreference = getActiveCustomerPreferences(state)
      menuSubtype = get(menu, 'subtype')
    } else {
      exemptionOverrides = makeExemptionOverridesFromOrder(order)
      orderingPreference = getOrderPreferencesByOrder(state, orderId)
      menuSubtype = order?.orderType
      paymentOnOrder = getPaymentByOrder(state, orderId)
    }
    paymentOptions = orderingPreference?.[menuSubtype]?.payments ?? []
  }

  // TODO (JEFF): TabOrderPayNow is always a new order, a lot of clean up can be done here
  if (isNewOrder) {
    return { ...newOrder(state, ownProps), displayName, paymentOptions, isNewOrder, isSuiteCustomer, orderingPreference, remoteOrderTotal, finalOrderState, printers, deviceReceiptPrinterUuid, isSuitesMode, peripheralStatus, discountsAvailable, cashEnabled, noOpPayments, isNoTipMenu}
  }

  if (OrderStates.isClosed(order.state)) {
    return { ...existingOrder(state, ownProps), displayName, paymentOptions, isSuiteCustomer, orderId, orderingPreference, remoteOrderTotal, finalOrderState, printers, deviceReceiptPrinterUuid, paymentOnOrder, isSuitesMode, peripheralStatus, discountsAvailable }
  } else {
    return { ...joinedOrders(state, ownProps), exemptionOverrides, displayName, paymentOptions, isNewOrder, isSuiteCustomer, orderId, orderingPreference, remoteOrderTotal, isRemotePayment, finalOrderState, printers, deviceReceiptPrinterUuid, paymentOnOrder, isSuitesMode, peripheralStatus, discountsAvailable }
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const orderId = get(ownProps, 'match.params.orderId', undefined)
  const isNewOrder = orderId === undefined || orderId === 'pay-now'
  const replace = get(ownProps, 'history.replace', () => {})
  const push = get(ownProps, 'history.push', () => {})

  return {
    onCashPayment: (exemptionOverrides, updateExemptions, userNotes) => {
      if (isNewOrder) {
        push('/change-calculator?tab', { exemptionOverrides, userNotes })
      } else {
        const updateType = updateExemptions ? 'addAndUpdateExemptions' : 'add'
        dispatch(applyOrderExemptions(orderId, exemptionOverrides))
        dispatch(startTabOrderAdjustment(orderId, updateType, updateExemptions))
        push(`/change-calculator/${orderId}?tab`, { exemptionOverrides })
      }
    },
    onClose: () => {
      dispatch(clearRemoteOrderTotal())

      if (isNewOrder) {
        replace('/tab-order')
      } else {
        replace(`/tab-order/${orderId}`)
      }
    },
    onCloseWithNewCard: (data, exemptionOverrides, updateExemptions) => {
      const updateType = updateExemptions ? 'addAndUpdateExemptions' : 'add'
      dispatch(applyOrderExemptions(orderId, exemptionOverrides))
      dispatch(startTabOrderAdjustmentAndClose({
        adjustmentType: updateType,
        cardReaderData: data,
        deferClose: true,
        newCard: true,
        orderId,
      }))
      dispatch(clearCart())
    },
    onCloseWithStoredCard: (exemptionOverrides, updateExemptions) => {
      const updateType = updateExemptions ? 'addAndUpdateExemptions' : 'add'
      dispatch(applyOrderExemptions(orderId, exemptionOverrides))
      dispatch(startTabOrderAdjustment(orderId, updateType, updateExemptions))
      dispatch(clearCart())
    },
    onDone: () => {
      dispatch(clearRemoteOrderTotal())
      replace('/tabs')
    },
    getRemoteOrderTotal: () => dispatch(getRemoteOrderTotal({ orderUuid: orderId })),
    onRemotePayment: (paymentPreference, exemptionOverrides, newOrderId, updateExemptions) => {
      if (isNewOrder) {
        dispatch(startTabWithRemotePayment({ paymentPreference, exemptionOverrides, orderId: newOrderId }))
      } else {
        dispatch(applyOrderExemptions(orderId, exemptionOverrides))
        dispatch(startCloseTabOrder({ orderId, paymentPreference, newCard: false, remotePayment: true, deferClose: true, updateExemptions }))
        dispatch(clearCart())
      }
    },
    onSuccess: (data, userNotes, exemptionOverrides, signatureRequired, isNoTipMenu) => {
      const params = {
        cardReaderData: data,
        userNotes,
        isNoTipMenu,
        signatureRequired,
        state: ORDER_STATES['TABBED'],
        exemptionOverrides,
      }
      dispatch(startOnlineCardOrder(params))
    },
    fetchPrinters: () => dispatch(fetchPrinters()),
    onViewCheck: (orderId) => push({ pathname: `/signature/${orderId}`, search: '?action=close', state: { isFromPayNow: true }}),
    printOrder: (params) => dispatch(printOrder(params)),
    onSelectNoOpPayment: (exemptionOverrides, userNotes) => push('/tab-order/pay-now/select-payment', { exemptionOverrides, userNotes }),
    onPlaceFreeOrder: () => dispatch(startCashOrder())
  }
}
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TabOrderPayNowModal))
